import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AppRoutes } from './routes';
import { InstallAppPrompt } from './components';
import { sessionManager } from './config/sessionManager';
import { sessionValidator } from './services/sessionValidator';
import './App.css';
import { isStandalonePWA } from './helpers/pwa';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = sessionManager.isAuthenticated();
      setIsAuthenticated(isAuth);

      // Start session validator if authenticated
      if (isAuth) {
        sessionValidator.start();

        // ALWAYS validate on page load/refresh to catch permission changes
        // This ensures if an admin changed your role/permissions, you see it immediately
        await sessionValidator.validateSession();
      }

      setIsLoading(false);
    };

    checkAuth();

    // Cleanup on unmount
    return () => {
      sessionValidator.stop();
    };
  }, []);

  const handleLogout = () => {
    sessionValidator.stop(); // Stop validator on logout
    sessionManager.clearSession();
    setIsAuthenticated(false);
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
      </div>
    );
  }

  return (
    <>
      <AppRoutes isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} onLogout={handleLogout} />
      <InstallAppPrompt enabled={isAuthenticated} />

      {/* PWA: show a floating refresh action button for both iOS and Android */}
      {isStandalonePWA() && (
        <button
          aria-label="Refresh"
          title="Refresh"
          onClick={() => window.location.reload()}
          className="fixed bottom-4 right-4 z-50 rounded-md bg-white/70 text-black shadow-lg w-12 h-12 flex items-center justify-center active:scale-95 transition-transform duration-150"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path d="M12 6V3L8 7l4 4V8c2.757 0 5 2.243 5 5a5 5 0 1 1-9.584-2H5.26A7 7 0 1 0 12 6z" />
          </svg>
        </button>
      )}

      {/* Global ToastContainer - only one needed for entire app */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

export default App;
